// src/utils/teamActivityText.js
// D-297 commit 5 (Ravi 4 Nov 2026) — every text on the Team Activity page in
// English (the page's default) and Roman Hinglish, chosen by the app-wide
// useLang() toggle (D-307).
//
// WHY THIS EXISTS: the Owner page follows the same Hinglish / English toggle as
// the field screens, but its texts are not field-screen texts, so they live
// here (pure, Jest in the standard src/utils run) instead of
// src/i18n/technicianStrings.js. The band / group words, the legend and the
// time texts come from activityMap.js (BAND_LABELS, groupLabel(), bandText(),
// whenText()). The WhatsApp message stays Roman Hinglish (nudgeMessage.js).
// Kept English in both languages (decision 4 Nov): action names ("PO approved"),
// role names (roleLabel()), source names in banners.
//
// {name} placeholders are filled from vars; a key missing in Hinglish falls
// back to English, a missing key shows the key (never a blank).

export const TEAM_TEXT = Object.freeze({
  title:            { en: "Team Activity",
                      hi: "Team Activity" },
  subtitle:         { en: "Last real action of each person in the last {days} days. Logins do not count.",
                      hi: "Har vyakti ka pichhle {days} din ka aakhri asli kaam. Login gina nahi jata." },
  live:             { en: "Live",
                      hi: "Live" },
  lastChange:       { en: "last change {time}",
                      hi: "aakhri badlav {time}" },
  refresh:          { en: "Refresh now",
                      hi: "Abhi refresh karein" },
  claimMissing:     { en: "Your login is not ready yet (company not confirmed). Wait a moment and press Refresh now.",
                      hi: "Aapka login abhi tayyar nahi hai (company confirm nahi hui). Thoda rukein aur \"Abhi refresh karein\" dabayein." },
  usersError:       { en: "The user list could not be read ({reason}). Press Refresh now.",
                      hi: "User list padh nahi paye ({reason}). \"Abhi refresh karein\" dabayein." },
  failedTitle:      { en: "Some activity could not be read — colours may be wrong:",
                      hi: "Kuch activity padh nahi paye — rang galat ho sakte hain:" },
  failedRetry:      { en: "Press Refresh now to try again.",
                      hi: "Dobara koshish ke liye \"Abhi refresh karein\" dabayein." },
  capped:           { en: "More than {limit} entries in {days} days for: {sources}. Older ones are not counted — someone may show older than they are.",
                      hi: "{days} din mein {limit} se zyada entries: {sources}. Purani entries nahi gini gayi — koi asal se zyada purana dikh sakta hai." },
  errIndex:         { en: "database index still being built — try again in a few minutes",
                      hi: "database index abhi ban raha hai — kuch minute baad koshish karein" },
  errPermission:    { en: "no permission to read",
                      hi: "padhne ki permission nahi hai" },
  errOther:         { en: "could not be read",
                      hi: "padh nahi paye" },
  loading:          { en: "Loading…",
                      hi: "Load ho raha hai…" },
  legendSundays:    { en: "Sundays are not counted. Nothing in {days} days = {red}.",
                      hi: "Sunday nahi gina jata. {days} din mein kuch nahi = {red}." },
  legendNotCounted: { en: "Not counted: accepting a job (starting it counts); item request decisions saved before 4 Nov 2026.",
                      hi: "Nahi gina jata: job accept karna (start karna gina jata hai); 4 Nov 2026 se pehle ke item request faisle." },
  noActiveUsers:    { en: "No active users.",
                      hi: "Koi active user nahi." },
  nobodyHere:       { en: "Nobody here.",
                      hi: "Yahan koi nahi." },
  showing:          { en: "Showing: {group}",
                      hi: "Dikh raha hai: {group}" },
  showAll:          { en: "Show all",
                      hi: "Sab dikhayein" },
  neverDesc:        { en: "Added as users, but have not signed in to Zeno yet.",
                      hi: "User bana diye gaye, par abhi tak Zeno mein login nahi kiya." },
  // Commit 6 (Ravi 4 Nov): amber / red / never-logged-in user with no valid mobile.
  noPhoneAdd:       { en: "📵 No phone — add number →",
                      hi: "📵 Phone nahi hai — number jodein →" },
  noPhoneOnly:      { en: "📵 No phone",
                      hi: "📵 Phone nahi hai" },
  inactiveDesc:     { en: "Switched off in User Management — not coloured.",
                      hi: "User Management mein band kiye gaye — inka rang nahi dikhta." },
  noActionInDays:   { en: "no action in {days} days",
                      hi: "{days} din mein koi kaam nahi" },
  unmatchedOne:     { en: "1 action not matched to a user of this company",
                      hi: "1 kaam is company ke kisi user se match nahi hua" },
  unmatchedMany:    { en: "{count} actions not matched to a user of this company",
                      hi: "{count} kaam is company ke kisi user se match nahi hue" },
  unmatchedDesc:    { en: "Made by someone not in the user list (for example SuperAdmin or a deleted user).",
                      hi: "Kisi aise vyakti ne kiya jo user list mein nahi hai (jaise SuperAdmin ya delete kiya gaya user)." },
  by:               { en: "by {who}",
                      hi: "{who} ne" },
  andMore:          { en: "…and {count} more.",
                      hi: "…aur {count}." },
  recentTitle:      { en: "Last {days} days — newest first (up to 5)",
                      hi: "Pichhle {days} din — naya pehle (5 tak)" },
  recentEmpty:      { en: "No action in the last {days} days.",
                      hi: "Pichhle {days} din mein koi kaam nahi." },
  noActionRecorded: { en: "No action recorded",
                      hi: "Koi kaam darj nahi" },
  noName:           { en: "(no name)",
                      hi: "(naam nahi)" },
  whatsapp:         { en: "WhatsApp",
                      hi: "WhatsApp" },
  whatsappTitle:    { en: "Opens your WhatsApp with this message filled in. Nothing is sent until you press Send.",
                      hi: "Aapka WhatsApp yeh message bhar kar khulega. Send dabane tak kuch nahi jayega." },
  ownerOnly:        { en: "Only the Owner can see Team Activity.",
                      hi: "Team Activity sirf Owner dekh sakte hain." },
  // Home card (commit 7, Ravi 4 Nov) — Dashboard.js, same toggle (default English).
  homeScore:        { en: "Team on Zeno: {using} / {total} using",
                      hi: "Team Zeno: {using} / {total} log use kar rahe" },
  homeRemind:       { en: "Remind {remind} →",
                      hi: "{remind} ko yaad dilayein →" },
  homeAllUsing:     { en: "🎉 Whole team is using Zeno",
                      hi: "🎉 Poori team Zeno use kar rahi hai" },
  homePctLabel:     { en: "{pct}% of the team is using Zeno",
                      hi: "{pct}% team Zeno use kar rahi hai" },
  homeFailed:       { en: "Could not check Team Activity →",
                      hi: "Team Activity check nahi ho paya →" },
  homeFailedHint:   { en: "Tap to open Team Activity",
                      hi: "Team Activity kholne ke liye tap karein" },
});

/**
 * Commit 6 (Ravi 4 Nov), widened in 8b: what an amber / red / never-logged-in
 * row shows when the user has no valid WhatsApp / login mobile (no WhatsApp
 * button). Since 8a Edit User has a "WhatsApp number" box for EVERY role (own
 * doc too), so every user with a users doc id gets the "add number →" link to
 * /users/edit/{users doc id} (the route User Management uses); without a doc id
 * there is nothing to open → the text alone (never a link to nowhere).
 * @returns {{ key: "noPhoneAdd"|"noPhoneOnly", href: string|null }}
 */
export const noPhoneHint = (user) => {
  const docId = String(user?.docId ?? "").trim();
  if (docId) {
    return { key: "noPhoneAdd", href: `/users/edit/${encodeURIComponent(docId)}` };
  }
  return { key: "noPhoneOnly", href: null };
};

/** The text for this key in this language ("hi" or English), placeholders filled. */
export const teamText = (key, lang, vars) => {
  const entry = TEAM_TEXT[key];
  let text = entry ? (lang === "hi" && entry.hi ? entry.hi : entry.en) : key;
  if (vars) {
    Object.keys(vars).forEach((k) => {
      text = text.split(`{${k}}`).join(String(vars[k]));
    });
  }
  return text;
};
